<script>
    import { http } from '@/system/http'
    import CountrySelect from '@/system/components/global/form/CountrySelect.vue'
    import { isValidZipCode } from '@/system/helpers/zipCodeValidation'

    export default {
        components: {
            CountrySelect,
        },
        props: {
            address: {
                type: String,
                required: false,
                default: null
            },
            address_line_2: {
                type: String,
                required: false,
                default: null
            },
            city: {
                type: String,
                required: false,
                default: null
            },
            grouped_events: {
                required: false,
                default: null
            },
            zip: {
                type: String,
                required: false,
                default: null
            },
            country: {
                type: String,
                required: false,
                default: ''
            },
            zone: {
                type: String,
                required: false,
                default: null
            },
            validation_errors: {
                required: false,
                default: () => {}
            },
            // If we want to display address_line_2 / country / zone fields
            showFullAddress: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        data () {
            return {
                isLoadingCity : false,
                showAddressLine2: Boolean(this.address_line_2),
                form: {
                    address: this.address,
                    address_line_2: this.address_line_2,
                    country: this.country,
                    city: this.city,
                    zone: this.zone,
                    zip: this.zip
                }
            }
        },
        computed: {
            showStateField () {
                return this.form.country
                    ? Config.get(`countries.${this.form.country}.address_state_visible`)
                    : true
            },
            showLine2Field () {
                return this.form.country
                    ? Config.get(`countries.${this.form.country}.address_line2_visible`)
                    : this.showFullAddress
            },
            addressLineLabel () {
                return this.showAddressLine2
                    ? trans('clients.address_line')
                    : trans('clients.address')
            }
        },
        watch: {
            showLine2Field (value) {
                if (!value) {
                    this.showAddressLine2 = false
                }
            },
            showAddressLine2 (value) {
                if (value) {
                    this.$nextTick(() => this.$refs.addressLine2.focus())
                } else {
                    this.form.address_line_2 = ''
                }
            },
            showStateField (value) {
                if (!value) {
                    this.form.zone = ''
                }
            },
            'form.address': function (value) {
                this.$emit('changeAddress', value)
            },
            'form.address_line_2': function (value) {
                this.$emit('changeAddressLine2', value)
            },
            'form.country': function (value) {
                this.$emit('changeCountry', value)
            },
            'form.zone': function (value) {
                this.$emit('changeZone', value)
            },
            'form.zip': function (value) {
                this.fetchCityFromZip(value)
                this.$emit('changeZip', value)
            },
            'form.city': function (value) {
                this.$emit('changeCity', value)
            },
            address (value) {
                this.form.address = value
            },
            address_line_2 (value) {
                this.form.address_line_2 = value
            },
            city (value) {
                this.form.city = value
            },
            zip (value) {
                this.form.zip = value
            },
            country (value) {
                this.form.country = value
            },
            zone (value) {
                this.form.zone = value
            },
            validation_errors: function(val) {
                this.$setErrorBag(val);
            },
        },

        methods: {
            async fetchCityFromZip(zip) {
                const country = this.form.country || locale;
                const isValidZip = isValidZipCode(zip, country)

                if (isValidZip) {
                    this.isLoadingCity = true;
                    this.errors.clear();

                    try {
                        const response = await http.get('/app/address/city/', { params: { zip, country } });

                        if (response.data !== '') {
                            this.form.city = response.data;
                        }
                    } finally {
                        this.isLoadingCity = false;
                    }
                }
            }
        },
    };
</script>

<template id="address-input">

    <div class="address-input">

        <div class="row">

            <!-- Address Line Input -->
            <div class="col-xs-12">
                <div class="form-group" :class="{ 'has-error' : errors.has('address') }">
                    <label class="control-label">{{ addressLineLabel }}</label>
                    <input class="form-control" value="" type="text" name="address" v-model="form.address">
                    <span v-show="errors.has('address')" class="help-block">{{ errors.first('address') }}</span>
                    <span v-show="!errors.has('address')" class="help-block text-muted" :style="{'justify-content': showFullAddress && showAddressLine2 ? 'space-between': 'flex-end'}" style="display: flex;">
                        <span v-if="showFullAddress && showAddressLine2">{{ trans('clients.address_line_description') }}</span>
                        <a v-if="showFullAddress && showLine2Field" href="#" @click.prevent="showAddressLine2 = !showAddressLine2">
                            {{ showAddressLine2 ? trans('clients.hide_line2') : trans('clients.add_line2') }}
                        </a>
                    </span>
                </div>
            </div>
        </div>

        <div class="row" v-if="showAddressLine2">

            <!-- Address Line 2 Input -->
            <div class="col-xs-12">
                <div class="form-group" :class="{ 'has-error' : errors.has('address_line_2') }">
                    <label for="address-line-2" class="control-label">{{ trans('clients.address_line_2') }}</label>
                    <input ref="addressLine2" class="form-control" value="" type="text" name="address-line-2" v-model="form.address_line_2">
                    <span v-show="errors.has('address_line_2')" class="help-block">{{ errors.first('address_line_2') }}</span>
                    <span v-show="!errors.has('address_line_2')" class="help-block text-muted">
                        {{ trans('clients.address_line_2_description') }}
                    </span>
                </div>
            </div>
        </div>

        <div class="row">

            <!-- Zip Input -->
            <div class="col-xs-12 col-sm-6">
                <div class="form-group" :class="{ 'has-error' : errors.has('zip') }">
                    <label class="control-label">{{ trans('clients.zip') }}</label>
                    <input class="form-control" value="" type="text" name="zip" v-model="form.zip">
                    <span v-show="errors.has('zip')" class="help-block">{{ errors.first('zip') }}</span>
                </div>
            </div>

            <!-- City Input -->
            <div class="col-xs-12 col-sm-6">
                <div class="form-group has-feedback" :class="{ 'has-error' : errors.has('city') }">
                    <label class="control-label">{{ trans('clients.city') }}</label>
                    <input class="form-control" value="" type="text"  name="city" v-model="form.city">
                    <i class="fa fa-circle-o-notch fa-spin form-control-feedback" style="width: auto;height: auto;right: 10px;" v-show="isLoadingCity"></i>
                    <span v-show="errors.has('city')" class="help-block">{{ errors.first('city') }}</span>
                </div>
            </div>
        </div>

        <div :class="{'row': showStateField }" v-if="showFullAddress">

            <!-- Zone Input -->
            <div v-if="showStateField" class="col-xs-12 col-md-6">
                <div class="form-group has-feedback" :class="{ 'has-error' : errors.has('zone') }">
                    <label class="control-label">{{ trans('clients.zone') }}</label>
                    <input class="form-control" type="text"  name="zone" v-model="form.zone">
                    <span v-show="errors.has('zone')" class="help-block">{{ errors.first('zone') }}</span>
                </div>
            </div>

            <!-- Country Input -->
            <div :class="{'col-xs-12 col-md-6': showStateField }">
                <div class="form-group" :class="{ 'has-error' : errors.has('country') }">
                    <label class="control-label">{{ trans('clients.country') }}</label>
                    <country-select v-if="$store.state.auth.owner" v-model="form.country"></country-select>
                    <span v-show="errors.has('country')" class="help-block">{{ errors.first('country') }}</span>
                </div>
            </div>
        </div>

    </div>

</template>


